import {
  Box,
  LinearProgress,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  styled,
} from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import { Fragment, ReactNode, Ref, useImperativeHandle } from 'react';

import { ColumnDef } from './Column';
import { Header } from './Header';
import { Pagination, PaginationProps } from './Pagination';
import { DetailRow, PrimaryRow } from './Row';
import { RowSelectionState, useRowSelection } from './useRowSelection';

type RowStateExtension = {
  disabled?: boolean;
  disabledRowSelection?: boolean;
};

export interface PaginatedTableProps<R, R2, C> {
  columns: readonly ColumnDef<R & RowStateExtension, C>[];
  getRowKey(row: R): string;
  rows: readonly (R & RowStateExtension)[];

  detailColumns?: readonly ColumnDef<R2 & RowStateExtension, C>[];
  getDetailRowKey?(row: R2): string;
  getDetailRows?(row: R): readonly (R2 & RowStateExtension)[];
  onRowClick?: (rowKey: string) => void;

  context: C;

  tableRef?: Ref<PaginatedTableHandle>;

  rowSelection?: RowSelectionState;
  pagination?: PaginationProps;

  loading?: boolean;
  emptyState?: ReactNode;
  sx?: SxProps<Theme>;
  filteredSelectedKeys?: string[];
  variant?: Variant;
}

export interface PaginatedTableHandle {
  /**
   * If no `detailColumns` were passed to the table, calling this function has
   * no effect.
   */
  expandRow(rowKey: string): void;
}

export function PaginatedTable<R, R2 = never, C = unknown>({
  columns,
  rows,
  detailColumns,
  context,
  tableRef,
  rowSelection,
  pagination,
  loading,
  emptyState,
  sx,
  variant,
  getRowKey,
  onRowClick,
  getDetailRows,
  getDetailRowKey,
  filteredSelectedKeys,
}: PaginatedTableProps<R, R2, C>) {
  if (detailColumns && (!getDetailRows || !getDetailRowKey)) {
    throw new Error(
      `Either all or none of the props 'detailColumns', 'getDetailRowKey' ` +
        `and 'getDetailRows' expected.`,
    );
  }

  const rowExpansion = useRowSelection(rows, getRowKey);
  useImperativeHandle(tableRef, () => ({
    expandRow(rowKey: string) {
      rowExpansion.set(rowKey, true);
    },
  }));

  function onPageChange(page: number) {
    rowExpansion.resetSelectedKeys();
    pagination?.onPageChange(page);
  }

  return (
    <Root
      sx={{
        border: (theme) => `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '16px',
        width: '100%',
        height: '100%',
        paddingTop: '8px',
        paddingBottom: '8px',
        ...sx,
      }}
    >
      <TableContainerStyled $hideRowBorders={!!detailColumns}>
        <Table stickyHeader>
          <TableHead>
            <Header
              key="header"
              columns={columns}
              rowSelection={rowSelection}
              context={context}
              filteredSelectedKeys={filteredSelectedKeys}
              disabledRowSelectionKeys={rows
                .filter((row) => row.disabledRowSelection)
                .map(getRowKey)}
            />

            {loading && (
              <tr>
                <LoadingTableCell
                  colSpan={getColumnCount({
                    columns,
                    detailColumns,
                    rowSelection,
                  })}
                >
                  <LinearProgress />
                </LoadingTableCell>
              </tr>
            )}
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={getColumnCount({
                    columns,
                    detailColumns,
                    rowSelection,
                  })}
                >
                  {emptyState}
                </TableCell>
              </TableRow>
            )}

            {rows.map((row) => {
              const rowKey = getRowKey(row);
              const disableRow = row?.disabled;
              const disabledRowSelection = row?.disabledRowSelection;

              return (
                <Fragment key={rowKey}>
                  <PrimaryRow
                    columns={columns}
                    row={row}
                    rowKey={rowKey}
                    context={context}
                    rowSelection={rowSelection}
                    disabledRowSelection={disabledRowSelection}
                    disableStripe={!!detailColumns}
                    rowExpansion={detailColumns && rowExpansion}
                    columnPadding={
                      detailColumns && getColumnPadding(detailColumns, columns)
                    }
                    disableRow={disableRow}
                    onRowClick={onRowClick}
                  />
                  {rowExpansion.selectedKeys.has(rowKey) &&
                    detailColumns &&
                    getDetailRows &&
                    getDetailRowKey && (
                      <>
                        {/* Render headers only for the first detail row */}
                        {getDetailRows(row).length > 0 && (
                          <DetailRow
                            key={`${getDetailRowKey(
                              getDetailRows(row)[0],
                            )}-header`}
                            columns={detailColumns}
                            row={getDetailRows(row)[0]}
                            context={context}
                            tableHasRowSelection={!!rowSelection}
                            columnPadding={getColumnPadding(
                              columns,
                              detailColumns,
                            )}
                            showHeaders={true}
                          />
                        )}
                        {/* Render all detail rows without headers */}
                        {getDetailRows(row).map((detailRow) => (
                          <DetailRow
                            key={getDetailRowKey(detailRow)}
                            columns={detailColumns}
                            row={detailRow}
                            context={context}
                            tableHasRowSelection={!!rowSelection}
                            columnPadding={getColumnPadding(
                              columns,
                              detailColumns,
                            )}
                          />
                        ))}
                      </>
                    )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainerStyled>
      {pagination && <Pagination {...pagination} onPageChange={onPageChange} />}
    </Root>
  );
}

const TableContainerStyled = styled(TableContainer)<{
  $hideRowBorders: boolean;
}>(({ $hideRowBorders }) => ({
  flex: '1 0 0',
  '.MuiTableCell-root': $hideRowBorders ? { border: 'none' } : {},
}));

const Root = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

const LoadingTableCell = styled('td')({
  position: 'sticky',
  top: 60,
  padding: 0,
});

function getColumnCount({
  columns,
  detailColumns,
  rowSelection,
}: Pick<
  PaginatedTableProps<never, never, never>,
  'columns' | 'detailColumns' | 'rowSelection'
>) {
  let result = getColumnDefColSpanSum(columns);
  if (detailColumns) {
    result = Math.max(result, getColumnDefColSpanSum(detailColumns));
    // "Expand details" button column
    result++;
  }
  // Checkbox column
  if (rowSelection) result++;
  return result;
}

function getColumnDefColSpanSum(
  columns: readonly Pick<ColumnDef<never, never>, 'colSpan'>[],
) {
  return columns.reduce((sum, column) => sum + (column.colSpan ?? 1), 0);
}

function getColumnPadding(
  left: readonly Pick<ColumnDef<never, never>, 'colSpan'>[],
  right: readonly Pick<ColumnDef<never, never>, 'colSpan'>[],
) {
  return Math.max(
    getColumnDefColSpanSum(left) - getColumnDefColSpanSum(right),
    0,
  );
}
